import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { OpenRoutes, NotFoundPage } from './open.routes';
import { PrivateRoutes, IPrivateRoutes } from './private.routes';
import { RequireAuth } from './require.auth';
import { useAuth } from '../providers/auth';

const AppRouter = () => {
  const { user, loading, hasPermission, isAdmin } = useAuth();

  // Enquanto carrega auth, não renderizar rotas privadas para evitar flash de 404
  if (loading) {
    return (
      <Routes>
        {OpenRoutes.map((item, key) => (
          <Route key={Number(key)} path={item.path} element={<item.component />} />
        ))}
        <Route path="*" element={<div />} />
      </Routes>
    );
  }

  // Verifica se o usuário tem permissão para acessar uma rota
  const canAccessRoute = (route: IPrivateRoutes): boolean => {
    // Admin sempre tem acesso
    if (isAdmin()) return true;

    // Rotas sem código são públicas (ex: home, perfil)
    if (!route.codigo) return true;

    // Rotas especiais que sempre devem ser acessíveis
    const rotasPublicas = ['perfil', 'seguranca', 'sessoes'];
    if (rotasPublicas.includes(route.codigo)) return true;

    // Verifica se o usuário tem qualquer permissão para a tela (ou sub-telas)
    return hasPermission(route.path);
  };

  return (
    <Routes>
      {OpenRoutes.map((item, key) => (
        <Route key={Number(key)} path={item.path} element={<item.component />} />
      ))}
      <Route path="/" element={<RequireAuth />}>
        {PrivateRoutes.filter(item => !item.permissionSympla)
          .filter(item => {
            // Filtros legados (mantidos para compatibilidade)
            if (user?.grupo_id === 2 && item.path.startsWith('/configuracoes')) {
              return false;
            }
            // Restringir acesso à configuração de bots apenas para grupo 1
            if (item.path === '/configuracao-bots' && user?.grupo_id !== 1) {
              return false;
            }
            // API Controller cria credenciais de acesso a PII — apenas admin (grupo 1)
            if (item.path === '/configuracoes/api-controller' && user?.grupo_id !== 1) {
              return false;
            }
            // MCP do sistema mostra auditoria de chamadas e a configuração do servidor —
            // apenas admin (grupo 1), mesmo que alguém libere a tela por engano na Central
            if (item.path === '/configuracoes/mcp' && user?.grupo_id !== 1) {
              return false;
            }
            // Grupo Desenvolvedor (5) não pode acessar rotas de usuários
            if (user?.grupo_id === 5 && item.path === '/configuracoes/usuarios') {
              return false;
            }

            // Verificação de permissão de tela
            return canAccessRoute(item);
          })
          .map((item, key) => {
            const Component = item.component as unknown as React.ElementType;
            const ComponentCreate = item.componentCreate as unknown as React.ElementType;
            const ComponentShow = item.componentShow as unknown as React.ElementType;
            const ComponentEdit = item.componentEdit as unknown as React.ElementType;

            // Verifica permissões específicas para cada ação
            const canCreate = isAdmin() || hasPermission(item.path, 'create');
            const canShow = isAdmin() || hasPermission(item.path, 'show');
            const canEdit = isAdmin() || hasPermission(item.path, 'edit');

            return (
              <React.Fragment key={Number(key)}>
                {Component && <Route path={item.path} element={<Component />} />}
                {ComponentCreate && canCreate && (
                  <Route path={`${item.path}/criar`} element={<ComponentCreate />} />
                )}
                {ComponentShow && canShow && (
                  <Route path={`${item.path}/:itemId`} element={<ComponentShow />} />
                )}
                {ComponentEdit && canEdit && (
                  <Route path={`${item.path}/:itemId/editar`} element={<ComponentEdit />} />
                )}
              </React.Fragment>
            );
          })}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRouter;
